import { useCallback } from 'react';

import { useLiveActivity } from '@order/integrations';

import { SUBSCRIPTION_TYPE } from '../constants';
import { type NotificationCategories, NotificationCategoryType } from './types';
import { useNotificationPermissions } from './useNotificationPermissions';
import { useNotificationPreferencesState } from './useNotificationPreferencesState';
import { useNotificationPreferencesUpdates } from './useNotificationPreferencesUpdates';

// ─────────────────────────────────────────────────────────────────────

export const useNotificationPreferences = () => {
  const state = useNotificationPreferencesState();
  const permissions = useNotificationPermissions();

  const { setNotificationPreferences, isUpdating } = useNotificationPreferencesUpdates(); // prettier-ignore
  const { cancelAllLiveActivities } = useLiveActivity();

  // ─────────────────────────────────────────────────────────────────────

  const mergeUpdatedPreferences = useCallback(
    (params: MergePreferencesParams) => {
      if (!state.preferences) return;

      const mergePreferences = mergePreferencesFactory(params);

      return state.preferences.reduce<NotificationCategories>(
        mergePreferences,
        [],
      );
    },
    [state.preferences],
  );

  const updateNotificationPreferences = useCallback(
    async (params: MergePreferencesParams) => {
      const updates = mergeUpdatedPreferences(params);

      if (!updates) return;

      const isOrderPushDisabled =
        params.category === NotificationCategoryType.OrderStatus &&
        params.subscriptionType === SUBSCRIPTION_TYPE.PUSH;

      const isSuccess = await setNotificationPreferences(updates);

      // NOTE: When the request succeeds and the user disables order-status push notifications,
      // we clear the current live activity from the device, since the user will no longer receive
      // live updates due to the updated notification preferences.
      if (isSuccess && isOrderPushDisabled) {
        cancelAllLiveActivities();
      }
    },
    [
      cancelAllLiveActivities,
      mergeUpdatedPreferences,
      setNotificationPreferences,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    preferences: state.preferences,
    error: state.error,
    isLoading: state.loading,
    refetch: state.refetch,
    permissions,
    isUpdating,
    updateNotificationPreferences,
  };
};

// ─── Helpers ──────────────────────────────────────────────────────────

function mergePreferencesFactory(params: MergePreferencesParams) {
  const { subscriptionType, category, value } = params;

  return function (
    categories: NotificationCategories,
    currentCategory: NotificationCategories[number],
  ): NotificationCategories {
    if (currentCategory.id !== category) {
      return [...categories, currentCategory];
    }

    return [...categories, { ...currentCategory, [subscriptionType]: value }];
  };
}

// ─── Types ───────────────────────────────────────────────────────────

type MergePreferencesParams = {
  category: string;
  subscriptionType: string;
  value: boolean;
};
